import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { format, parse } from "date-fns";
import { Pressable } from "react-native";
import { useFragment } from "react-relay";

import { RainIcon } from "@whoosh/design-system/src/components/icons_v2/weather";
import {
  HStack,
  Box,
  VStack,
  Text,
} from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";

import { Raincheck_raincheck$key } from "./__generated__/Raincheck_raincheck.graphql";

type RaincheckProps = {
  fragmentRef: Raincheck_raincheck$key | null;
  onPress?: () => void;
};

export const Raincheck = ({ fragmentRef, onPress }: RaincheckProps) => {
  const raincheck = useFragment(RaincheckFragmentString, fragmentRef);

  const squareId = raincheck?.squareGiftCard?.squareId ?? "";

  const expirationDate = raincheck?.expirationDate;
  const formattedExpirationDate = expirationDate
    ? format(parse(expirationDate, "yyyy-MM-dd", new Date()), "MMMM d, yyyy")
    : "";
  const balance = raincheck?.squareGiftCard?.balance ?? 0;
  const limitRedemptionToFacility =
    raincheck?.facility.raincheckConfiguration.limitRedemptionToFacility;

  const { currencySymbol } = useGetClubCurrency();

  return (
    <Pressable
      testID={`raincheck-${squareId}`}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={
        expirationDate
          ? t`Raincheck expiring ${formattedExpirationDate}`
          : t`Raincheck`
      }
    >
      <HStack py={2} justifyContent="space-between" alignItems="center" gap={2}>
        <Box
          bg="surface-secondary"
          width={40}
          height={26}
          borderRadius="2"
          justifyContent="center"
          alignItems="center"
        >
          <RainIcon />
        </Box>
        <VStack flexGrow={1}>
          <Text variant="mainBaseSemiBold">{t`Raincheck${
            expirationDate ? ` expiring ${formattedExpirationDate}` : ""
          }`}</Text>
          <Text variant="mainTiny" color="text-secondary">
            {t`Balance`}: {currencySymbol}
            {ConvertCentsToDollars(balance)}
          </Text>
          {limitRedemptionToFacility ? (
            <Text variant="mainTiny" color="text-secondary">
              {t`Valid at`}: {raincheck?.facility.name}
            </Text>
          ) : null}
        </VStack>
      </HStack>
    </Pressable>
  );
};

export const RaincheckFragmentString = graphql`
  fragment Raincheck_raincheck on Raincheck {
    id
    expirationDate
    facility {
      id
      name
      raincheckConfiguration {
        limitRedemptionToFacility
      }
    }
    squareGiftCard {
      id

      balance
      createdAt
      squareId
    }
  }
`;
